'use client'

import { useRef } from 'react'
import { useTrackElementInteracted, useTrackElementViewed } from '@nwwp/segment-analytics'
import { Button, type buttonVariants, cn, type VariantProps } from '@nwwp/ui-shadcn'

type Props = {
  href?: string
  target?: string
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  'aria-label'?: string
  id?: string
  type?: 'button' | 'submit' | 'reset'

  /**
   * The name of the element to track.
   *
   * @default "ButtonCTA"
   */
  segmentElement?: string

  /**
   * When true, scrolls to top of page on click.
   */
  scrollToTop?: boolean

  onClick?: React.MouseEventHandler<HTMLButtonElement>
} & VariantProps<typeof buttonVariants>

export const ButtonCTA = ({
  href,
  target,
  children,
  segmentElement,
  scrollToTop,
  onClick,
  className,
  variant,
  size,
  type,
  ...props
}: Props) => {
  const trackElementInteracted = useTrackElementInteracted()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const viewEventId = useTrackElementViewed(buttonRef, {
    element: segmentElement ?? 'ButtonCTA',
    href,
  })

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Tracking is best-effort — never block navigation / caller onClick
    try {
      await trackElementInteracted({
        element: segmentElement ?? 'ButtonCTA',
        href,
        parentEventId: viewEventId.current ?? undefined,
      })
    } catch {
      // ignore analytics failures
    }

    if (href) {
      if (target === '_blank') {
        const a = document.createElement('a')
        a.href = href
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        a.click()
      } else {
        window.location.href = href
      }
    }

    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    onClick?.(e)
  }

  const resolvedVariant = variant ?? 'cta'
  const resolvedSize = size ?? (resolvedVariant === 'cta' || resolvedVariant === 'cta-dark' ? 'cta' : undefined)

  return (
    <Button
      ref={buttonRef}
      onClick={handleClick}
      variant={resolvedVariant}
      size={resolvedSize}
      className={cn(className)}
      type={type}
      {...props}
    >
      {children}
    </Button>
  )
}
